import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Building2, ChevronDown, Search } from 'lucide-react';
import { orgCreatedMs, readOrgSort, saveOrgSort } from '../utils/orgSort';

function DeveloperOrgSelector() {
  const {
    isDeveloper,
    allOrganizations,
    selectedDeveloperOrg,
    selectDeveloperOrg,
    loadAllOrganizations,
    companyOrganizations = [],
    companyEntries,
    selectCompanyEntry,
    selectedSiteGroup,
    selectCompanyOrg,
    userData
  } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState(readOrgSort);
  const dropdownRef = useRef(null);

  // Load organizations when component mounts
  useEffect(() => {
    if (isDeveloper) {
      loadAllOrganizations();
    }
  }, [isDeveloper]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only show for developers
  const companyMode = !isDeveloper && companyOrganizations.length > 1;
  // SH9: in company mode each entry is a business that runs reachable sites (see utils/companyEntries.js)
  const entries = companyEntries || companyOrganizations.map(org => ({ ...org, key: org.id, orgId: org.id }));
  const organizations = companyMode ? entries : allOrganizations;
  const currentGroup = selectedSiteGroup && selectedSiteGroup.orgId === userData?.orgId ? selectedSiteGroup.groupKey : null;
  const selectedId = companyMode
    ? (currentGroup && entries.some(entry => entry.key === `${userData?.orgId}::${currentGroup}`)
      ? `${userData?.orgId}::${currentGroup}` : userData?.orgId)
    : selectedDeveloperOrg?.orgId;
  if (!isDeveloper && !companyMode) {
    return null;
  }

  // Filter orgs based on search term (show all orgs, including demo)
  const changeSort = (mode) => {
    setSortMode(mode);
    saveOrgSort(mode);
  };

  // Developer list arrives A-Z (AuthContext); "Newest" puts the latest sign-ups first.
  const sortedOrgs = !companyMode && sortMode === 'newest'
    ? [...organizations].sort((a, b) => orgCreatedMs(b) - orgCreatedMs(a))
    : organizations;

  const filteredOrgs = sortedOrgs.filter(org => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      org.name?.toLowerCase().includes(search) ||
      org.id?.toLowerCase().includes(search)
    );
  });

  const handleSelectOrg = (org) => {
    if (companyMode) {
      if (selectCompanyEntry) selectCompanyEntry(org);
      else selectCompanyOrg(org.orgId || org.id);
    } else selectDeveloperOrg(org.id, org.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  const selectedOrgName = companyMode
    ? (organizations.find(o => o.key === selectedId)?.name || userData?.orgName || 'Select Organization')
    : (selectedDeveloperOrg?.orgName || organizations.find(o => o.id === selectedId)?.name || 'Select Organization');

  return (
    <div ref={dropdownRef} style={styles.container}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={styles.button}
        title="Select organization to view"
        aria-expanded={isOpen}
      >
        <Building2 size={16} style={{ color: '#6366f1' }} />
        <span style={styles.orgName}>{selectedOrgName}</span>
        <ChevronDown size={14} style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s'
        }} />
      </button>

      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.searchBox}>
            <Search size={14} style={{ color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
              autoFocus
            />
          </div>

          {!companyMode && (
            <div style={styles.sortRow} role="group" aria-label="Sort organizations">
              {[['name', 'A–Z'], ['newest', 'Newest']].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => changeSort(mode)}
                  aria-pressed={sortMode === mode}
                  style={{ ...styles.sortButton, ...(sortMode === mode ? styles.sortButtonActive : null) }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div style={styles.orgList}>
            {filteredOrgs.length === 0 ? (
              <div style={styles.noResults}>No organizations found</div>
            ) : (
              filteredOrgs.map(org => (
                <button
                  key={companyMode ? org.key : org.id}
                  onClick={() => handleSelectOrg(org)}
                  style={{
                    ...styles.orgItem,
                    backgroundColor: selectedId === (companyMode ? org.key : org.id) ? '#eef2ff' : 'transparent'
                  }}
                >
                  <div style={styles.orgInfo}>
                    <span style={styles.orgItemName}>{org.name}</span>
                    <span style={styles.orgType}>
                      {org.type === 'demo' ? '(Demo)' : ''}
                      {org.subscription?.status === 'suspended' ? ' - Suspended' : ''}
                      {org.entitlement?.label ? ` ${org.entitlement.label}` : ''}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    position: 'relative',
    marginRight: '12px'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: '#f5f3ff',
    border: '1px solid #c7d2fe',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    color: '#4338ca',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  orgName: {
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  dropdown: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: '0',
    width: '280px',
    maxWidth: 'calc(100vw - 24px)',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    overflow: 'hidden'
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    backgroundColor: 'transparent'
  },
  sortRow: {
    display: 'flex',
    gap: '4px',
    padding: '6px 12px',
    borderBottom: '1px solid #e5e7eb'
  },
  sortButton: {
    flex: 1,
    padding: '4px 8px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: '#6b7280',
    fontSize: '12px',
    cursor: 'pointer'
  },
  sortButtonActive: {
    backgroundColor: '#eef2ff',
    border: '1px solid #c7d2fe',
    color: '#4338ca',
    fontWeight: '600'
  },
  orgList: {
    maxHeight: '300px',
    overflowY: 'auto'
  },
  orgItem: {
    width: '100%',
    textAlign: 'left',
    border: 0,
    padding: '10px 12px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    borderBottom: '1px solid #f3f4f6'
  },
  orgInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  orgItemName: {
    overflowWrap: 'anywhere',
    fontSize: '13px',
    color: '#111827',
    fontWeight: '500'
  },
  orgType: {
    fontSize: '11px',
    color: '#9ca3af'
  },
  noResults: {
    padding: '16px',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '13px'
  }
};

// Shared with the developer menu preview (pages/developer/MenuPreview.js) so its picture matches this control.
export { styles as orgSelectorStyles };
export default DeveloperOrgSelector;
